// La table de marque (D-175, D-176) côté base : ouvrir la table (un seul téléphone), envoyer le journal dans l'ordre,
// demander, passer ou reprendre la main ; la fiche du match (staff) ; le suivi et la feuille sans compte.
// Toutes les fonctions sont des RPC (supabase/migrations/20261108100000_table_marque.sql) : la table ne se lit jamais
// directement, l'empreinte de l'appareil ne sort pas de la base.
import { supabase } from './supabase';
import type { Action } from '../domain/boxscore';
import type { Op } from '../domain/table';
import type { TeamGender } from '../domain/labels';
import { ClockSync } from '../domain/game-live';

/** L'horloge de la base vue de ce téléphone (celle des jeux, D-167) : chaque réponse qui porte l'heure de la base la recale.
 *  Tous les chronos de la table sont à cette heure-là : un téléphone hors réseau garde le dernier écart et compte juste. */
class TableClock extends ClockSync { measured = false; override add(sent: number, recv: number, server: number): void { super.add(sent, recv, server); this.measured = true; } }
export const tableClock = new TableClock();

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
/** Erreur de la table : refusée (« denied » : un autre téléphone la tient, ou pas le droit), fermée (match terminé), réseau. */
export class TableError extends Error { constructor(message: string, readonly kind: 'denied' | 'closed' | 'network') { super(message); } }
async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  let res: { data: unknown; error: { message: string; code?: string } | null };
  const sent = Date.now();
  try { res = await db().rpc(fn, args); } catch (e) { throw new TableError(String((e as Error)?.message ?? e), 'network'); }
  const now = (res.data as { now?: unknown } | null)?.now;
  if (!res.error && typeof now === 'number') tableClock.add(sent, Date.now(), now);
  if (res.error) {
    const c = res.error.code;
    throw new TableError(res.error.message, c === '42501' ? 'denied' : c === '22023' ? 'closed' : 'network');
  }
  return res.data as T;
}

export interface TableMatch {
  event_id: string; label: string; team_name: string; gender: TeamGender; category: string | null; opponent: string; is_home: boolean | null;
  competition: string | null; starts_at: string; location: string | null; live: boolean; quarter: number; numbers: Record<string, number>;
  club: string; club_short: string | null; logo: string | null; color: string | null;
}
export interface TablePlayer { person_id: string; name: string; jersey: number | null }
export interface TableServer {
  held: boolean; mine: boolean; holder_name: string | null; holder_seen: string | null; asking: boolean; ask: boolean;
  state: unknown; state_at: string | null; clock_ms: number | null; clock_run: boolean; public_token: string | null; window: boolean; now: number;
  match: TableMatch; players: TablePlayer[]; actions: Action[];
  /** Une horloge commune qui pilote la table (lot 3 : les terrains d'un tournoi) ; null le plus souvent. */
  common: unknown;
}

export const tableState = (event: string, link: string | null, key: string | null) =>
  rpc<TableServer>('table_state', { p_event: event, p_link: link, p_key: key });
export const tableOpen = (event: string, link: string | null, key: string, name: string | null, state: unknown) =>
  rpc<TableServer>('table_open', { p_event: event, p_link: link, p_key: key, p_name: name, p_state: state });
export const tableAsk = (event: string, link: string | null, key: string, name: string | null) =>
  rpc<TableServer>('table_ask', { p_event: event, p_link: link, p_key: key, p_name: name });
/** Les gestes dans l'ordre, puis l'état (chrono, période, flèche) ; `at` : l'heure du téléphone, pour l'ordre et le chrono. */
export function tableSync(event: string, key: string, ops: Op[], state: unknown) {
  const now = Date.now();
  const wire = ops.map(o => (o.op === 'add'
    ? { op: 'add', id: o.a.id, kind: o.a.kind, person_id: o.a.person_id, opp_number: o.a.opp_number ?? null, quarter: o.a.quarter, game_ms: o.a.game_ms ?? null,
        meta: o.a.meta ?? null, at: Date.parse(o.a.created_at) || now }
    : o));
  return rpc<{ done: number; skipped: string[]; now: number }>('table_sync', { p_event: event, p_key: key, p_ops: wire, p_state: state, p_now: now });
}
export const tablePass = (event: string, key: string) => rpc<string>('table_pass', { p_event: event, p_key: key });
export const tableTake = (token: string, key: string, name: string | null) => rpc<string>('table_take', { p_token: token, p_key: key, p_name: name });

/** Ce dont la table de marque a besoin côté base (D-176) : la même table sert un match du club (table_* : le staff, le
 *  lien de saisie) et un match de tournoi (tmatch_* : le téléphone qui tient le terrain, D-177). */
export interface TableBackend {
  state(key: string): Promise<TableServer>;
  open(key: string, name: string | null, state: unknown): Promise<TableServer>;
  ask(key: string, name: string | null): Promise<TableServer>;
  sync(key: string, ops: Op[], state: unknown): Promise<{ done: number; skipped: string[]; now: number }>;
  pass(key: string): Promise<string>;
  /** L'adresse du QR « Passer la main ». */
  passUrl(token: string): string;
}
/** La table d'un match du club. */
export const matchBackend = (event: string, link: string | null): TableBackend => ({
  state: key => tableState(event, link, key),
  open: (key, name, state) => tableOpen(event, link, key, name, state),
  ask: (key, name) => tableAsk(event, link, key, name),
  sync: (key, ops, state) => tableSync(event, key, ops, state),
  pass: key => tablePass(event, key),
  passUrl: token => `${location.origin}/t/${token}`,
});

// --- La fiche du match (staff) -----------------------------------------------------------------------------------------
export interface TableAdmin {
  held: boolean; holder_name: string | null; holder_seen: string | null; ask: boolean; ask_name: string | null; ask_at: string | null;
  public_token: string; sheet_token: string | null; state: unknown; now: number;
}
export const tableAdmin = (event: string) => rpc<TableAdmin | null>('table_admin', { p_event: event });
export const tableAnswer = (event: string, accept: boolean) => rpc<boolean>('table_answer', { p_event: event, p_accept: accept });
export const tableRelease = (event: string) => rpc<null>('table_release', { p_event: event });
export const tableSheetLink = (event: string) => rpc<string>('table_sheet_link', { p_event: event });

// --- Sans compte ---------------------------------------------------------------------------------------------------------
export interface TablePublic {
  club: string; club_short: string | null; logo: string | null; color: string | null; label: string; opponent: string; is_home: boolean | null;
  competition: string | null; location: string | null; starts_at: string; live: boolean; gender: TeamGender;
  final: { us: number; them: number } | null; state: { rules?: unknown; period?: number; phase?: string; arrow?: string | null; finished?: boolean; colors?: { us?: string | null; them?: string | null } };
  clock_ms: number | null; clock_run: boolean; state_at: string | null; now: number;
  actions: Pick<Action, 'kind' | 'quarter' | 'game_ms'>[];
}
export const tablePublic = (token: string) => rpc<TablePublic | null>('table_public', { p_token: token });
export interface TableSheet {
  match: Omit<TableMatch, 'numbers'>; minors: boolean; state: unknown;
  players: { key: string; num: number | null; name?: string }[];
  actions: { kind: Action['kind']; quarter: number; game_ms: number | null; opp_number: number | null; player: string | null; created_at: string; meta: Action['meta'] }[];
}
export const tableSheet = (token: string) => rpc<TableSheet | null>('table_sheet', { p_token: token });
